import { OptLogVolumeActions, OptLogVolumeActionsDetailsResponse, OptLogVolumeActionsTypes } from '~/shared/types/api';

export type ActionActorKind = 'system' | 'rule' | 'user' | 'unknownUser';

export interface ActionActorDisplay {
  kind: ActionActorKind;
  label: string;
  ruleId?: string;
  tooltip?: string;
}

const automaticActionTypes = new Set<OptLogVolumeActionsTypes>(['Growth', 'Shrink']);

export function isAutomaticActionType(type: OptLogVolumeActionsTypes): boolean {
  return automaticActionTypes.has(type);
}

export function isTriggeredActionType(type: OptLogVolumeActionsTypes): boolean {
  return !isAutomaticActionType(type);
}

export function getActionActorKey(action: Pick<OptLogVolumeActions, 'volumeId' | 'actionFinishTime'>): string {
  return `${action.volumeId}-${action.actionFinishTime}`;
}

export function getActionActorDisplay(action: Pick<OptLogVolumeActions, 'type' | 'ruleId' | 'userName'>): ActionActorDisplay {
  if (isAutomaticActionType(action.type)) {
    return { kind: 'system', label: 'System' };
  }

  if (action.ruleId) {
    return {
      kind: 'rule',
      label: action.ruleId,
      ruleId: action.ruleId,
      tooltip: action.ruleId,
    };
  }

  if (action.userName) {
    return {
      kind: 'user',
      label: action.userName,
      tooltip: action.userName,
    };
  }

  return { kind: 'unknownUser', label: 'User' };
}

export function needsActionActorDetailsFetch(
  actions: Pick<OptLogVolumeActions, 'type' | 'ruleId' | 'userName'>[]
): boolean {
  return actions.some(
    (action) =>
      isTriggeredActionType(action.type) && !action.ruleId && !action.userName
  );
}

export function mergeActionActorDetails(
  actions: OptLogVolumeActions[],
  details: Pick<OptLogVolumeActions, 'volumeId' | 'actionFinishTime' | 'ruleId' | 'userName' | 'userEmail'>[]
): OptLogVolumeActions[] {
  if (details.length === 0) {
    return actions;
  }

  const detailsByKey = new Map(
    details.map((detail) => [getActionActorKey(detail), detail] as const)
  );

  let changed = false;
  const merged = actions.map((action) => {
    const detail = detailsByKey.get(getActionActorKey(action));
    if (!detail) {
      return action;
    }

    const ruleId = action.ruleId ?? detail.ruleId;
    const userName = action.userName ?? detail.userName;
    const userEmail = action.userEmail ?? detail.userEmail;

    if (
      ruleId === action.ruleId &&
      userName === action.userName &&
      userEmail === action.userEmail
    ) {
      return action;
    }

    changed = true;
    return {
      ...action,
      ruleId,
      userName,
      userEmail,
    };
  });

  return changed ? merged : actions;
}

export function toOptLogActionsDetailsUrl(tableQueryUrl: string): string {
  if (tableQueryUrl.includes('/api/v1/opt-log/actions/details')) {
    return tableQueryUrl;
  }

  const [path, query = ''] = tableQueryUrl.split('?');
  const detailsPath = path.replace('/api/v1/opt-log/actions', '/api/v1/opt-log/actions/details');
  return query ? `${detailsPath}?${query}` : detailsPath;
}

export interface EnrichActionsWithActorDetailsParams {
  actions: OptLogVolumeActions[];
  listQueryUrl: string;
  canReadUsers: boolean;
  fetchDetails: (url: string) => Promise<OptLogVolumeActionsDetailsResponse>;
}

export async function enrichActionsWithActorDetails({
  actions,
  listQueryUrl,
  canReadUsers,
  fetchDetails,
}: EnrichActionsWithActorDetailsParams): Promise<OptLogVolumeActions[]> {
  if (!canReadUsers || !needsActionActorDetailsFetch(actions)) {
    return actions;
  }

  const response = await fetchDetails(toOptLogActionsDetailsUrl(listQueryUrl));
  return mergeActionActorDetails(actions, response.actions ?? []);
}
